import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import { API_DELETE, API_POST, DBRequest, useConfig } from '../../api/api';
import ExoDialog from '../exo/ExoDialog';
import ExoAvatar from '../exo/ExoAvatar';
import { ExoAutoCompleteSelector } from '../exo/ExoAutoCompleteSelector';
import Loader from '../special/Loader';

export const ECAssignLead = ({ onChange, email }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const interaction = email.interactions?.[0];

  if (!interaction) return null;

  const participants = interaction.participants || [];

  function updateInteraction(updatedInteraction) {
    onChange({
      data: {
        ...email,
        interactions: email.interactions.map((element) =>
          element.id === updatedInteraction.id ? updatedInteraction : element,
        ),
      },
    });
  }

  function handleAdd(data, type) {
    DBRequest({
      config,
      path: `interactions/${interaction.id}/participants`,
      method: API_POST({ type, participant: { id: data.id } }),
      onResponse: (res) => updateInteraction(res.data),
      onLoading: setIsLoading,
    });
  }

  function handleRemove(participantData) {
    DBRequest({
      config,
      path: `interactions/${interaction.id}/participants/${participantData.id}`,
      method: API_DELETE(),
      onResponse: () =>
        updateInteraction({
          ...interaction,
          participants: participants.filter(
            (element) => element.id !== participantData.id,
          ),
        }),
      onLoading: setIsLoading,
    });
  }

  return (
    <>
      <Loader active={isLoading} />
      <Tooltip title={t('Assign to lead')}>
        <Button
          onClick={() => setOpen(true)}
          sx={{
            minWidth: 0,
            padding: '6px',
            border: `1px solid ${colors.info}80`,
            ':hover': {
              bgcolor: colors.info + '30',
            },
          }}
        >
          <PersonAddAltIcon />
        </Button>
      </Tooltip>

      <ExoDialog open={open} onClose={() => setOpen(false)} limitWidth>
        <Box className="flex flex-col gap-4 p-4 h-full overflow-hidden">
          <Typography variant="h4" textAlign="center">
            {t('Assign to lead')}
          </Typography>
          <ExoAutoCompleteSelector
            label={t('Add Participant')}
            onSelectionChange={handleAdd}
            multiselect={false}
            searchParams={['person_leads', 'company_leads']}
          />
          <Box className="flex flex-col gap-1 overflow-y-auto">
            <Typography fontWeight={500} fontSize="12px" className="opacity-60">
              {`${t('Participants')} (${participants.length})`}
            </Typography>
            {participants.map(
              (participantData) =>
                participantData.participant && (
                  <ParticipantRow
                    key={participantData.id}
                    data={participantData}
                    onRemove={() => handleRemove(participantData)}
                  />
                ),
            )}
          </Box>
        </Box>
      </ExoDialog>
    </>
  );
};

const ParticipantRow = ({ data, onRemove }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { participant, type } = data;

  const fullName = [participant?.lastName, participant?.firstName]
    .filter(Boolean)
    .join(' ');

  const nameMap = {
    companyLead: participant.nameShort || participant.name,
    personLead: fullName,
    company: participant.nameShort || participant.name,
    contact: fullName,
    user: fullName,
  };

  const typeLabelMap = {
    companyLead: t('Company Lead'),
    personLead: t('Person Lead'),
    company: t('Company'),
    contact: t('Contact'),
    user: t('Employee'),
  };

  const routeMap = {
    companyLead: `/leads/companies/${participant.id}`,
    personLead: `/leads/persons/${participant.id}`,
    company: `/companies/${participant.id}`,
    contact: `/contacts/${participant.id}`,
    user: `/employees/${participant.id}`,
  };

  return (
    <Box
      className="flex items-center justify-between gap-2 rounded-lg px-2 py-1"
      sx={{ bgcolor: colors.bgInput }}
    >
      <Box className="flex items-center gap-2">
        <ExoAvatar name={nameMap[type]} size="35px" />
        <Box className="flex flex-col">
          <Typography fontSize="10px" fontWeight={600} className="opacity-50">
            {typeLabelMap[type]}
          </Typography>
          <NavLink to={routeMap[type]}>
            <Typography fontWeight={500}>{nameMap[type]}</Typography>
          </NavLink>
        </Box>
      </Box>
      <IconButton color="error" onClick={onRemove}>
        <DeleteIcon sx={{ fontSize: '16px' }} />
      </IconButton>
    </Box>
  );
};
